import React, { useEffect, useState } from "react";
import { Card, CardBody, Container, Row, Col, Button, Input, Label, Modal, ModalHeader, ModalBody, Spinner } from "reactstrap";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import { getBestSellingAdBanners, addBestSellingAdBanner, deleteBestSellingAdBanner, updateBestSellingAdBanner } from "../../api";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:5001/api";

const BestSellingAdBanner = () => {
  document.title = "Best Selling Ad Space - Dashboard";
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [sortOrder, setSortOrder] = useState(0);
  const [imageFile, setImageFile] = useState(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);

  const fetchBanners = async () => {
    setLoading(true);
    try {
      const data = await getBestSellingAdBanners();
      setBanners(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err?.error || err?.message || "Failed to load best selling ad banners");
      setBanners([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBanners();
  }, []);

  const toggle = () => {
    setModal(!modal);
    setEditingBanner(null);
    setLinkUrl("");
    setSortOrder(0);
    setImageFile(null);
    setTitle("");
    setDescription("");
  };

  const handleEditClick = (banner) => {
    setEditingBanner(banner);
    setLinkUrl(banner.link_url || "");
    setSortOrder(banner.sort_order || 0);
    setImageFile(null);
    setTitle(banner.title || "");
    setDescription(banner.description || "");
    setModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!editingBanner && !imageFile) {
      toast.error("Please select an image");
      return;
    }
    setSubmitting(true);
    try {
      const formData = new FormData();
      if (imageFile) {
        formData.append("bestSellingAdImage", imageFile);
      }
      formData.append("link_url", linkUrl);
      formData.append("sort_order", sortOrder);
      formData.append("title", title);
      formData.append("description", description);

      if (editingBanner) {
        await updateBestSellingAdBanner(editingBanner.id, formData);
        toast.success("Best selling ad banner updated successfully");
      } else {
        await addBestSellingAdBanner(formData);
        toast.success("Best selling ad banner added successfully");
      }
      toggle();
      fetchBanners();
    } catch (err) {
      toast.error(err?.error || err?.message || `Failed to ${editingBanner ? "update" : "add"} best selling ad banner`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this best selling ad banner?")) return;
    try {
      await deleteBestSellingAdBanner(id);
      toast.success("Best selling ad banner deleted");
      fetchBanners();
    } catch (err) {
      toast.error(err?.error || "Failed to delete best selling ad banner");
    }
  };

  const getImageUrl = (banner) => {
    if (!banner.image) return null;
    if (banner.image.startsWith("http")) return banner.image;
    const base = (API_BASE || "").replace("/api", "");
    return `${base}${banner.image.startsWith("/") ? "" : "/"}${banner.image}`;
  };

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="Best Selling Ad" pageTitle="Website" />
        <Row>
          <Card>
            <CardBody>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                  <h5 className="card-title mb-1">Best Selling Ad Banners & Promotions</h5>
                  <p className="text-muted small mb-0">Manage promotional ads and banners displayed on the Best Selling Items page and section.</p>
                </div>
                <Button color="primary" onClick={toggle}>
                  <i className="ri-add-line align-bottom me-1"></i> Upload Best Selling Ad
                </Button>
              </div>
              {loading ? (
                <div className="text-center py-5">
                  <Spinner /> Loading...
                </div>
              ) : banners.length === 0 ? (
                <div className="text-center py-5 text-muted border border-dashed rounded">
                  <i className="ri-advertisement-line display-4 text-secondary mb-3"></i>
                  <h5>No Best Selling Ad Banners Yet</h5>
                  <p className="mb-3">Upload your first best selling ad banner to highlight top promotional deals.</p>
                  <Button color="primary" size="sm" onClick={toggle}>
                    <i className="ri-add-line align-bottom me-1"></i> Add New Ad Banner
                  </Button>
                </div>
              ) : (
                <Row>
                  {banners.map((b) => (
                    <Col md={6} lg={4} key={b.id} className="mb-4">
                      <div className="border rounded-3 p-3 position-relative bg-white shadow-sm h-100 d-flex flex-column justify-content-between">
                        <div>
                          <div className="position-relative rounded overflow-hidden mb-3" style={{ height: "180px", backgroundColor: "#f8f9fa" }}>
                            <img
                              src={getImageUrl(b)}
                              alt={b.title || "Best Selling Ad"}
                              className="img-fluid w-100 h-100 rounded"
                              style={{ objectFit: "cover" }}
                            />
                            <span className="badge bg-primary position-absolute top-0 start-0 m-2">
                              Order: {b.sort_order}
                            </span>
                          </div>
                          {b.title && (
                            <h6 className="fw-bold text-dark mb-1">{b.title}</h6>
                          )}
                          {b.description && (
                            <p className="small text-muted mb-2 line-clamp-2">{b.description}</p>
                          )}
                          {b.link_url && (
                            <div className="small text-primary mb-2 text-truncate">
                              <i className="ri-link me-1"></i>
                              <a href={b.link_url} target="_blank" rel="noopener noreferrer" className="text-primary text-decoration-none">
                                {b.link_url}
                              </a>
                            </div>
                          )}
                        </div>
                        <div className="d-flex gap-2 pt-2 border-top mt-2">
                          <Button
                            color="info"
                            size="sm"
                            className="flex-fill"
                            onClick={() => handleEditClick(b)}
                          >
                            <i className="ri-pencil-line align-bottom me-1"></i> Edit
                          </Button>
                          <Button
                            color="danger"
                            size="sm"
                            className="flex-fill"
                            onClick={() => handleDelete(b.id)}
                          >
                            <i className="ri-delete-bin-line align-bottom me-1"></i> Delete
                          </Button>
                        </div>
                      </div>
                    </Col>
                  ))}
                </Row>
              )}
            </CardBody>
          </Card>
        </Row>

        <Modal isOpen={modal} toggle={toggle} centered>
          <ModalHeader toggle={toggle}>
            {editingBanner ? "Edit Best Selling Ad Banner" : "Upload Best Selling Ad Banner"}
          </ModalHeader>
          <ModalBody>
            <form onSubmit={handleSubmit}>
              <div className="mb-3">
                <Label className="form-label">
                  Banner Image {editingBanner ? "(Leave empty to keep current)" : <span className="text-danger">*</span>}
                </Label>
                <Input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setImageFile(e.target.files[0] || null)}
                />
                <small className="text-muted">Recommended size: 1200x400px (Wide Banner) or 600x400px</small>
              </div>
              <div className="mb-3">
                <Label className="form-label">Banner Title</Label>
                <Input
                  type="text"
                  placeholder="e.g. Summer Mega Sale - Up to 50% Off"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </div>
              <div className="mb-3">
                <Label className="form-label">Description / Subtitle</Label>
                <Input
                  type="textarea"
                  rows={2}
                  placeholder="e.g. Discover our best-selling electronics and fashion items with exclusive discounts."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>
              <div className="mb-3">
                <Label className="form-label">Target Link URL (optional)</Label>
                <Input
                  type="text"
                  placeholder="e.g. /shop?category=1 or https://topcoinland.com/shop"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                />
              </div>
              <div className="mb-3">
                <Label className="form-label">Sort Order</Label>
                <Input
                  type="number"
                  placeholder="0"
                  value={sortOrder}
                  onChange={(e) => setSortOrder(parseInt(e.target.value, 10) || 0)}
                />
                <small className="text-muted">Lower number appears first</small>
              </div>
              <div className="d-flex justify-content-end gap-2">
                <Button color="secondary" type="button" onClick={toggle}>
                  Cancel
                </Button>
                <Button color="primary" type="submit" disabled={submitting}>
                  {submitting ? (
                    <>
                      <Spinner size="sm" /> Saving...
                    </>
                  ) : editingBanner ? (
                    "Save Changes"
                  ) : (
                    "Upload Banner"
                  )}
                </Button>
              </div>
            </form>
          </ModalBody>
        </Modal>

        <ToastContainer autoClose={2500} />
      </Container>
    </div>
  );
};

export default BestSellingAdBanner;
